<template>
  <v-container fluid>
    <v-card-title class="title primary white--text mb-4 text-capitalize">
        Title Request Stats
    </v-card-title>
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Top Make Distribution</v-card-title>
          <v-card-text>
            <bar-chart v-if="!loading" :chart-data="vehicleMakeChartData" :options="chartOptions"/>
            <v-skeleton-loader v-else type="image"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Top Model Distribution</v-card-title>
          <v-card-text>
            <bar-chart v-if="!loading" :chart-data="bikeModelChartData" :options="chartOptions"/>
            <v-skeleton-loader v-else type="image"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Type Distribution</v-card-title>
          <v-card-text>
            <pie-chart v-if="!loading" :chart-data="bikeTypeChartData" :options="chartOptions"/>
            <v-skeleton-loader v-else type="image"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Year Range Distribution</v-card-title>
          <v-card-text>
            <pie-chart v-if="!loading" :chart-data="vehicleYearRangeChartData" :options="chartOptions"/>
            <v-skeleton-loader v-else type="image"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Location Distribution (State)</v-card-title>
          <v-card-text>
            <bar-chart v-if="!loading" :chart-data="locationChartData" :options="chartOptions"/>
            <v-skeleton-loader v-else type="image"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Requests Per Month (Last 24 Months)</v-card-title>
          <v-card-text>
            <bar-chart v-if="!loading" :chart-data="requestsPerMonthChartData" :options="chartOptions"/>
            <v-skeleton-loader v-else type="image"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>Top 50 People by Title Requests</v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Title Count</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(person, idx) in topPeople" :key="person.key">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ person.name }}</td>
                  <td>{{ person.email }}</td>
                  <td>{{ person.count }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import fb from "@/main";
import { Bar, Pie, mixins } from "vue-chartjs";
const { reactiveProp } = mixins;

// Chart components
const BarChart = {
  extends: Bar,
  mixins: [reactiveProp],
  props: ["chartData", "options"],
  mounted() {
    this.renderChart(this.chartData, this.options);
  }
};
const PieChart = {
  extends: Pie,
  mixins: [reactiveProp],
  props: ["chartData", "options"],
  mounted() {
    this.renderChart(this.chartData, this.options);
  }
};

export default {
  name: "Stats",
  components: {
    BarChart,
    PieChart
  },
  data() {
    return {
      titles: [],
      loading: true,
      bikeModelChartData: null,
      locationChartData: null,
      bikeTypeChartData: null,
      vehicleMakeChartData: null,
      vehicleYearRangeChartData: null,
      requestsPerMonthChartData: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true }
      }
    };
  },
  computed: {
    topPeople() {
      // Group by email, count, and grab a name from one of the records
      const peopleMap = {};
      this.titles.forEach(t => {
        const email = (t.email || "").trim().toLowerCase();
        if (!peopleMap[email]) {
          const name = `${t.firstName || ""} ${t.lastName || ""}`.trim();
          peopleMap[email] = { name, email, count: 0, key: email };
        }
        peopleMap[email].count += 1;
      });
      return Object.values(peopleMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 50);
    }
  },
  async created() {
    await this.fetchData();
    this.prepareCharts();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      const snapshot = await fb.db.collection("titleRequests").get();
      this.titles = [];
      snapshot.forEach(doc => {
        const data = {...doc.data()};
        if (data.status === "completed") {
            this.titles.push({ id: doc.id, ...data });
        }
      });
      this.loading = false;
    },
    prepareCharts() {
      // Bike Model Distribution
      const modelCounts = {};
      this.titles.forEach(t => {
        const model = (t.vehicleModel || "Unknown").trim();
        modelCounts[model] = (modelCounts[model] || 0) + 1;
      });
      const topModels = Object.entries(modelCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 20);
      this.bikeModelChartData = {
        labels: topModels.map(([model]) => model),
        datasets: [
          {
            label: "Count",
            backgroundColor: "#26a69a",
            data: topModels.map(([, count]) => count)
          }
        ]
      };

      // Bike Type Distribution (now as pie chart)
      const typeCounts = {};
      this.titles.forEach(t => {
        const type = t.vehicleType || "Unknown";
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });
      const typeEntries = Object.entries(typeCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 20);
      this.bikeTypeChartData = {
        labels: typeEntries.map(([type]) => type),
        datasets: [
          {
            label: "Count",
            backgroundColor: [
              "#1976d2", "#26a69a", "#ef5350", "#ffa726", "#ab47bc",
              "#8d6e63", "#42a5f5", "#66bb6a", "#ff7043", "#d4e157"
            ],
            data: typeEntries.map(([, count]) => count)
          }
        ]
      };

      // Vehicle Make Distribution
      const makeCounts = {};
      this.titles.forEach(t => {
        const make = (t.vehicleMake || "Unknown").trim();
        makeCounts[make] = (makeCounts[make] || 0) + 1;
      });
      const topMakes = Object.entries(makeCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 20);
      this.vehicleMakeChartData = {
        labels: topMakes.map(([make]) => make),
        datasets: [
          {
            label: "Count",
            backgroundColor: "#1976d2",
            data: topMakes.map(([, count]) => count)
          }
        ]
      };

      // Location Distribution (State)
      const stateCounts = {};
      this.titles.forEach(t => {
        const state = t.state || "Unknown";
        stateCounts[state] = (stateCounts[state] || 0) + 1;
      });
      // Show all states, sorted by count descending
      const allStates = Object.entries(stateCounts)
        .sort((a, b) => b[1] - a[1]);
      this.locationChartData = {
        labels: allStates.map(([state]) => state),
        datasets: [
          {
            label: "Count",
            backgroundColor: "#1976d2",
            data: allStates.map(([, count]) => count)
          }
        ]
      };

      // Vehicle Year Range Distribution (pie chart)
      const yearCounts = {};
      this.titles.forEach(t => {
        let year = parseInt(t.vehicleYear, 10);
        if (!isNaN(year) && year > 1800 && year < 2100) {
          const decade = Math.floor(year / 10) * 10;
          const label = `${decade}-${decade + 9}`;
          yearCounts[label] = (yearCounts[label] || 0) + 1;
        } else {
          yearCounts["Unknown"] = (yearCounts["Unknown"] || 0) + 1;
        }
      });
      // Sort ranges by decade start
      const sortedRanges = Object.entries(yearCounts)
        .sort((a, b) => {
          if (a[0] === "Unknown") return 1;
          if (b[0] === "Unknown") return -1;
          return parseInt(a[0]) - parseInt(b[0]);
        });
      this.vehicleYearRangeChartData = {
        labels: sortedRanges.map(([range]) => range),
        datasets: [
          {
            label: "Count",
            backgroundColor: [
              "#1976d2", "#26a69a", "#ef5350", "#ffa726", "#ab47bc",
              "#8d6e63", "#42a5f5", "#66bb6a", "#ff7043", "#d4e157",
              "#90caf9", "#f06292", "#a1887f", "#c5e1a5", "#ffd54f"
            ],
            data: sortedRanges.map(([, count]) => count)
          }
        ]
      };

      // Requests Per Month (Last 24 Months)
      const now = new Date();
      const months = [];
      const monthLabels = [];
      const monthCounts = {};
      // Build last 24 months array
      for (let i = 23; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const label = `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}`;
        months.push(label);
        monthLabels.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
        monthCounts[label] = 0;
      }
      this.titles.forEach(t => {
        let dateObj = null;
        if (t.createDate && typeof t.createDate.toDate === "function") {
          dateObj = t.createDate.toDate();
        } else if (t.createDate instanceof Date) {
          dateObj = t.createDate;
        } else if (typeof t.createDate === "string" || typeof t.createDate === "number") {
          dateObj = new Date(t.createDate);
        }
        if (dateObj && !isNaN(dateObj.getTime())) {
          const label = `${dateObj.getFullYear()}-${('0' + (dateObj.getMonth() + 1)).slice(-2)}`;
          if (monthCounts[label] !== undefined) {
            monthCounts[label]++;
          }
        }
      });
      this.requestsPerMonthChartData = {
        labels: monthLabels,
        datasets: [
          {
            label: "Requests",
            backgroundColor: "#26a69a",
            data: months.map(m => monthCounts[m])
          }
        ]
      };
    }
  },
  watch: {
    titles() {
      this.prepareCharts();
    }
  }
};
</script>

<style scoped>
.v-card {
  min-height: 350px;
}
</style>
